/**
 * Tiny helpers for converting between base64 image payloads and Blobs
 * the browser knows how to copy/download.
 *
 * The backend `app.icon.list` returns raw base64 PNG bodies (no
 * `data:` prefix) to keep the JSON terse. The picker modal wraps those
 * in a Blob for clipboard / download — and the same util will be
 * useful for the upcoming `app.icon.video` exporter, so we keep it
 * standalone.
 */

/** Convert a base64 string into a `Blob` of the given MIME type. */
export function base64ToBlob(base64: string, mime = 'image/png'): Blob {
  const bin = atob(base64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type: mime });
}

/**
 * Guess MIME from a `data:image/...;base64,...` URL or a raw extension.
 * Defaults to `image/png` for the typical APK icon case.
 */
export function guessImageMime(input: string): string {
  const dataUrlMatch = input.match(/^data:(image\/[a-z0-9+]+);/i);
  if (dataUrlMatch) return dataUrlMatch[1].toLowerCase();
  if (/\.jpe?g$/i.test(input)) return 'image/jpeg';
  if (/\.webp$/i.test(input)) return 'image/webp';
  if (/\.gif$/i.test(input)) return 'image/gif';
  return 'image/png';
}

/**
 * Trigger a browser download for `blob` with the given filename.
 * Uses a transient `<a>` so it works in every modern browser without
 * needing the File System Access API.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the download dialog a tick to read the URL before we revoke.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Copy `blob` to the system clipboard as an image. Requires HTTPS or
 * `chrome://flags#unsafely-treat-insecure-origin-as-secure` for local
 * dev — the modal surfaces the rejection reason in a toast.
 */
export async function copyImageToClipboard(blob: Blob): Promise<void> {
  if (!navigator.clipboard || !window.ClipboardItem) {
    throw new Error('当前浏览器或运行环境不支持图片复制到剪贴板（需要 HTTPS）');
  }
  const item = new ClipboardItem({ [blob.type]: blob });
  await navigator.clipboard.write([item]);
}
